/**
 * Centralized Color Constants - projectL
 *
 * This file maps commonly used colors to semantic names.
 * Use these instead of hardcoding hex values throughout the app.
 *
 * For MUI theme integration, import from theme/theme.js
 * For quick access to commonly used colors, use this file.
 *
 * @module constants/colors
 */

// ============================================================================
// BRAND COLORS
// ============================================================================

export const BRAND = {
  primary: '#000000', // Main brand color (black)
  primaryLight: '#2d2d2d', // Lighter variant
  primaryDark: '#000000', // Darker variant
  accent: '#667eea', // Purple accent (used in some legacy components)
  accentHover: '#5a6fd6', // Hover state for accent
};

// ============================================================================
// STATUS COLORS
// ============================================================================

export const STATUS = {
  // Success/Delivered
  success: '#4caf50',
  successLight: '#e8f5e9',
  successDark: '#388e3c',
  delivered: {
    bg: '#dcfce7',
    text: '#166534',
    border: '#86efac',
  },

  // Warning/Pending/In Transit
  warning: '#ff9800',
  warningLight: '#fff3e0',
  warningDark: '#f57c00',
  pending: {
    bg: '#fef3c7',
    text: '#92400e',
    border: '#fcd34d',
  },
  inTransit: {
    bg: '#fffbe6',
    text: '#f7b500',
    border: '#fde68a',
  },

  // Info/Assigned
  info: '#2196f3',
  infoLight: '#e3f2fd',
  infoDark: '#1976d2',
  assigned: {
    bg: '#dbeafe',
    text: '#1e40af',
    border: '#93c5fd',
  },

  // Error/Failed/Cancelled
  error: '#f44336',
  errorLight: '#ffebee',
  errorDark: '#d32f2f',
  cancelled: {
    bg: '#fee2e2',
    text: '#991b1b',
    border: '#fca5a5',
  },
  failed: {
    bg: '#fee2e2',
    text: '#991b1b',
    border: '#fca5a5',
  },

  // Neutral
  neutral: '#9e9e9e',
  neutralLight: '#f5f5f5',
  neutralDark: '#616161',
};

// ============================================================================
// TEXT COLORS
// ============================================================================

export const TEXT = {
  primary: '#0f172a', // Main text color (dark)
  secondary: '#64748b', // Secondary text (muted)
  muted: '#94a3b8', // Very muted text
  disabled: '#cbd5e1', // Disabled state
  inverse: '#ffffff', // White text on dark bg
  link: '#2563eb', // Link color
  linkHover: '#1d4ed8', // Link hover
};

// ============================================================================
// BACKGROUND COLORS
// ============================================================================

export const BACKGROUND = {
  page: '#fafafa', // Page background
  paper: '#ffffff', // Card/paper background
  elevated: '#ffffff', // Elevated surfaces
  subtle: '#f8fafc', // Subtle background
  overlay: 'rgba(0,0,0,0.5)', // Modal overlay
  skeleton: '#e2e8f0', // Skeleton loading
};

// Surface tokens (backwards compatibility with other projects)
export const SURFACE = {
  // Primary surface color (paper/card backgrounds)
  paper: BACKGROUND.paper,
  // Background alias for legacy usage
  background: BACKGROUND.page,
  elevated: BACKGROUND.elevated,
  subtle: BACKGROUND.subtle,
  overlay: BACKGROUND.overlay,
};

// Action colors used for buttons/CTA in UI
export const ACTION = {
  primary: '#10b981', // green (success-ish)
  primaryHover: '#0ea56f',
  danger: '#f44336',
  dangerHover: '#d32f2f',
  subtle: '#e6fffa',
};

// ============================================================================
// BORDER COLORS
// ============================================================================

export const BORDER = {
  light: '#e2e8f0', // Light border
  default: '#cbd5e1', // Default border
  dark: '#94a3b8', // Dark border
  focus: '#3b82f6', // Focus ring
  divider: '#f1f5f9', // Divider lines
};

// ============================================================================
// INVOICE/PRINT COLORS (for PrintableInvoice)
// ============================================================================

export const INVOICE = {
  headerText: '#1a202c',
  labelText: '#64748b',
  bodyText: '#374151',
  accentPurple: '#667eea',
  bgMuted: '#f8fafc',
  border: '#e5e7eb',
};

// ============================================================================
// GRADIENTS
// ============================================================================

export const GRADIENTS = {
  primary: 'linear-gradient(135deg, #000000 0%, #2d2d2d 100%)',
  hero: 'linear-gradient(135deg, #000000 0%, #333333 100%)',
  page: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
  card: 'linear-gradient(180deg, #ffffff 0%, #fafafa 100%)',
  purple: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
};

// ============================================================================
// SHADOWS
// ============================================================================

export const SHADOWS = {
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
  card: '0 2px 8px rgba(0, 0, 0, 0.08)',
  elevated: '0 4px 12px rgba(0, 0, 0, 0.12)',
};

// ============================================================================
// UTILITY FUNCTION
// ============================================================================

/**
 * Get status color based on status string
 * @param {string} status - Parcel/order status
 * @param {string} [variant='text'] - 'text' | 'bg' | 'border'
 * @returns {string} Color code
 */
export function getStatusColor(status, variant = 'text') {
  const statusUpper = (status || '').toUpperCase();

  const statusMap = {
    PENDING: STATUS.pending,
    ASSIGNED: STATUS.assigned,
    PICKED_UP: STATUS.assigned,
    IN_TRANSIT: STATUS.inTransit,
    OUT_FOR_DELIVERY: STATUS.inTransit,
    DELIVERED: STATUS.delivered,
    COMPLETED: STATUS.delivered,
    CANCELLED: STATUS.cancelled,
    FAILED: STATUS.failed,
    RETURNED: { bg: '#efebe9', text: '#6d4c41', border: '#d7ccc8' },
  };

  const colors = statusMap[statusUpper] || {
    bg: STATUS.neutralLight,
    text: STATUS.neutral,
    border: STATUS.neutral,
  };

  if (variant === 'bg') return colors.bg;
  if (variant === 'border') return colors.border;
  return colors.text;
}

/**
 * Create a color with alpha transparency
 * @param {string} hex - Hex color
 * @param {number} alpha - Alpha value 0-1
 * @returns {string} RGBA color
 */
export function withAlpha(hex, alpha) {
  if (!hex || alpha == null) return hex;

  // Handle shorthand hex
  let fullHex = hex;
  if (hex.length === 4) {
    fullHex = '#' + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3];
  }

  const r = parseInt(fullHex.slice(1, 3), 16);
  const g = parseInt(fullHex.slice(3, 5), 16);
  const b = parseInt(fullHex.slice(5, 7), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// ============================================================================
// DEFAULT EXPORT (for convenience)
// ============================================================================

const colors = {
  BRAND,
  STATUS,
  TEXT,
  BACKGROUND,
  SURFACE,
  ACTION,
  BORDER,
  INVOICE,
  GRADIENTS,
  SHADOWS,
  getStatusColor,
  withAlpha,
};

export default colors;
